/**
 * WebhookTestPage — test form for the generic signed webhook channel.
 * notify POSTs the JSON payload as-is to the given endpoints; with a secret
 * every delivery carries the Stripe-style X-Notify-Signature header.
 */

import React, { useState } from "react";
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { Box, Grid, IconButton, TextField } from "@mui/material";
import {
  AddOutlined as AddIcon,
  DeleteOutline as DeleteIcon,
} from "@mui/icons-material";
import { useNotify, useTranslate } from "react-admin";
import { extractErrorMessage } from "@/utils/extractErrorMessage";
import { sendWebhookTestNotification } from "../services/testService";
import AccountAppPicker from "./sections/AccountAppPicker";
import RgSection from "./sections/RgSection";
import TestPageShell from "./TestPageShell";

interface WebhookFormValues {
  accountId: string;
  appId: string;
  webhookUrls: { value: string }[];
  event: string;
  payload: string;
  secret: string;
}

const emptyValues = (): WebhookFormValues => ({
  accountId: "",
  appId: "",
  webhookUrls: [{ value: "" }],
  event: "",
  payload: '{\n  "hello": "world"\n}',
  secret: "",
});

export const WebhookTestPage: React.FC = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const [submitting, setSubmitting] = useState(false);

  const methods = useForm<WebhookFormValues>({
    defaultValues: emptyValues(),
    mode: "all",
    reValidateMode: "onChange",
    shouldFocusError: true,
  });
  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: "webhookUrls",
  });

  const onSubmit = async (values: WebhookFormValues): Promise<void> => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(values.payload);
    } catch {
      notify("resources.test.webhook.payloadInvalid", { type: "error" });
      return;
    }
    setSubmitting(true);
    try {
      const response = await sendWebhookTestNotification({
        accountId: values.accountId,
        appId: values.appId,
        webhookUrls: values.webhookUrls
          .map((u) => u.value.trim())
          .filter(Boolean),
        event: values.event.trim() || undefined,
        payload: parsed,
        secret: values.secret.trim() || undefined,
        origin: "admin-test",
      });
      if (response.success !== false) {
        notify("resources.test.notifications.webhookSent", {
          type: "success",
        });
      } else {
        notify(
          response.message ??
            translate("resources.test.notifications.webhookSendFailed"),
          { type: "error" },
        );
      }
    } catch (err: unknown) {
      notify(
        extractErrorMessage(
          err,
          "resources.test.notifications.webhookSendFailed",
          translate,
        ),
        { type: "error" },
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormProvider {...methods}>
      <TestPageShell
        titleKey="resources.test.webhook.title"
        submitting={submitting}
        onSubmit={methods.handleSubmit(onSubmit)}
      >
        <RgSection
          label={translate("resources.test.sections.accountApplication", {
            _: "Account & application",
          })}
        >
          <AccountAppPicker />
        </RgSection>

        <RgSection
          label={translate("resources.test.teamChat.destination", {
            _: "Destination",
          })}
        >
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            <Grid item xs={12} md={8}>
              {fields.map((f, idx) => (
                <Box
                  key={f.id}
                  sx={{
                    display: "flex",
                    gap: 1,
                    mb: idx < fields.length - 1 ? 1.5 : 0,
                  }}
                >
                  <Controller
                    name={`webhookUrls.${idx}.value`}
                    control={methods.control}
                    rules={{
                      required: translate(
                        "resources.test.teamChat.webhookUrlRequired",
                      ),
                      pattern: {
                        value: /^https:\/\/.+/i,
                        message: translate(
                          "resources.test.teamChat.webhookUrlHttps",
                        ),
                      },
                    }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        fullWidth
                        required
                        label={`${translate("resources.test.webhook.urlField")} ${fields.length > 1 ? idx + 1 : ""}`.trim()}
                        placeholder="https://mi-sistema.com/hooks/notify"
                        InputLabelProps={{ shrink: true }}
                        error={Boolean(fieldState.error)}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                  {fields.length > 1 && (
                    <IconButton
                      aria-label={translate("ra.action.remove")}
                      onClick={() => remove(idx)}
                      sx={{ alignSelf: "center" }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                  {idx === fields.length - 1 && fields.length < 10 && (
                    <IconButton
                      aria-label={translate("ra.action.add")}
                      onClick={() => append({ value: "" })}
                      sx={{ alignSelf: "center" }}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              ))}
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name="event"
                control={methods.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={translate("resources.test.webhook.eventField")}
                    placeholder="booking.confirmed"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </RgSection>

        <RgSection
          label={translate("resources.test.webhook.payloadSection", {
            _: "Payload",
          })}
          last
        >
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            <Grid item xs={12} md={8}>
              <Controller
                name="payload"
                control={methods.control}
                rules={{
                  required: translate("resources.test.webhook.payloadRequired"),
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    fullWidth
                    required
                    multiline
                    minRows={6}
                    label={translate("resources.test.webhook.payloadField")}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ style: { fontFamily: "monospace", fontSize: 13 } }}
                    error={Boolean(fieldState.error)}
                    helperText={
                      fieldState.error?.message ??
                      translate("resources.test.webhook.payloadHelp")
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name="secret"
                control={methods.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={translate("resources.test.webhook.secretField")}
                    helperText={translate("resources.test.webhook.secretHelp")}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </RgSection>
      </TestPageShell>
    </FormProvider>
  );
};

export default WebhookTestPage;
